import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, ChevronRight, CookingPot, Plus } from 'lucide-react';

import { Button } from '@/components/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { EmptyState } from '@/components/EmptyState';
import { EMPTY_ACTION_CLASS } from '@/components/emptyStateClasses';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { FoodThumb } from '@/components/food/FoodRow';
import { formatGrams, formatKcal, formatNumber } from '@/components/food/format';
import { useRecipeList } from '@/lib/queries/recipes';

export const Route = createFileRoute('/_app/food/recipes/')({
  component: RecipesPage,
});

/**
 * Tus recetas.
 *
 * Cada tarjeta enseña lo que se decide de un vistazo —las kcal y los macros de
 * **una ración**, no de la olla entera— porque es lo que se apunta en el
 * diario.
 */
function RecipesPage() {
  const query = useRecipeList();
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader
        title="Recetas"
        subtitle="Lo que cocinas, con sus macros ya calculadas."
        action={
          <div className="flex gap-2">
            <Link
              to="/food"
              search={{}}
              aria-label="Volver al diario"
              className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
            >
              <ArrowLeft className="size-5" aria-hidden="true" />
            </Link>
            <Link
              to="/food/recipes/new"
              aria-label="Nueva receta"
              className="flex size-11 items-center justify-center rounded-xl bg-brand text-brand-ink"
            >
              <Plus className="size-5" aria-hidden="true" />
            </Link>
          </div>
        }
      />

      {query.isPending ? (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-raised p-4 text-ink-muted">
          <Spinner size="sm" />
          <span className="text-sm">Cargando tus recetas…</span>
        </div>
      ) : query.isError ? (
        <ErrorBanner
          title="No hemos podido cargar tus recetas"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={CookingPot}
          title="Aún no tienes recetas"
          description="Crea una con sus ingredientes y la app calculará las calorías y macros por ración. Luego podrás apuntarla en el diario por raciones."
          action={
            <Link to="/food/recipes/new" className={EMPTY_ACTION_CLASS}>
              <Plus className="size-4" aria-hidden="true" />
              Crear mi primera receta
            </Link>
          }
        />
      ) : (
        <ul className="flex flex-col gap-2 xs:grid xs:grid-cols-2 xl:grid-cols-3">
          {items.map((recipe) => (
            <li key={recipe.id}>
              <Link
                to="/food/recipes/$id"
                params={{ id: recipe.id }}
                className="flex h-full min-h-18 items-center gap-3 rounded-2xl border border-line bg-surface-raised p-2.5 transition-colors hover:bg-surface-overlay active:bg-surface-overlay"
              >
                <FoodThumb imageUrl={recipe.imageUrl} className="size-14" />
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 font-medium text-ink">{recipe.name}</span>
                  <span className="mt-0.5 block text-sm text-ink-muted tabular-nums">
                    {formatKcal(recipe.kcalPerServing)} · {formatNumber(recipe.servings)}{' '}
                    {recipe.servings === 1 ? 'ración' : 'raciones'}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-ink-faint tabular-nums">
                    P {formatGrams(recipe.proteinGPerServing)} · C{' '}
                    {formatGrams(recipe.carbsGPerServing)} · G {formatGrams(recipe.fatGPerServing)}
                  </span>
                </span>
                <ChevronRight className="size-5 shrink-0 text-ink-faint" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {query.hasNextPage ? (
        <Button
          variant="secondary"
          block
          className="mt-3"
          loading={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          Cargar más
        </Button>
      ) : null}
    </>
  );
}
